import React from 'react'
import {
  useFonts,
  BaiJamjuree_300Light,
  BaiJamjuree_300Light_Italic,
  BaiJamjuree_400Regular,
  BaiJamjuree_400Regular_Italic,
  BaiJamjuree_600SemiBold,
  BaiJamjuree_700Bold
} from '@expo-google-fonts/bai-jamjuree'

// component & screens
import Router from './src/routes/Router'
import {
  Image,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
  SafeAreaView,
  TouchableOpacity,
  Linking
} from 'react-native'
import { colorPalette, globalStyles } from './src/static/styles/global'

// stores
import { useAuthStore } from './src/stores/auth'

import Toast from 'react-native-toast-message'
import { toastConfig } from './src/utils/toastConfig'

// Helper function to inject default font styles into Text and TextInput components globally
const overrideDefaultFont = () => {
  const getFontFamily = (style: any) => {
    const flatStyle = StyleSheet.flatten(style) || {}
    const family = flatStyle.fontFamily

    if (family) {
      const lowerFamily = family.toLowerCase()
      if (
        lowerFamily.includes('opensans') ||
        lowerFamily.includes('arial') ||
        lowerFamily.includes('helvetica') ||
        lowerFamily.includes('sans-serif') ||
        lowerFamily.includes('aptos')
      ) {
        // Fall through to determine the correct Bai Jamjuree variant
      } else {
        return family
      }
    }

    let weight = flatStyle.fontWeight
    if (!weight && family) {
      const lowerFamily = family.toLowerCase()
      if (lowerFamily.includes('700') || lowerFamily.includes('bold')) {
        weight = '700'
      } else if (lowerFamily.includes('600') || lowerFamily.includes('semibold')) {
        weight = '600'
      } else if (lowerFamily.includes('300') || lowerFamily.includes('light')) {
        weight = '300'
      }
    }

    const isItalic = flatStyle.fontStyle === 'italic'

    if (
      weight === 'bold' ||
      weight === '700' ||
      weight === '800' ||
      weight === '900'
    ) {
      return 'BaiJamjuree_700Bold'
    }
    if (weight === '600' || weight === '500') {
      return 'BaiJamjuree_600SemiBold'
    }
    if (weight === '300' || weight === '100' || weight === '200') {
      return isItalic ? 'BaiJamjuree_300Light_Italic' : 'BaiJamjuree_300Light'
    }

    return isItalic ? 'BaiJamjuree_400Regular_Italic' : 'BaiJamjuree_400Regular'
  }

  // Override defaultProps style fallback
  const defaultStyle = { fontFamily: 'BaiJamjuree_400Regular' }

  if ((Text as any).defaultProps) {
    ;(Text as any).defaultProps.style = { ...defaultStyle, ...((Text as any).defaultProps.style || {}) }
  } else {
    ;(Text as any).defaultProps = { style: defaultStyle }
  }

  if ((TextInput as any).defaultProps) {
    ;(TextInput as any).defaultProps.style = { ...defaultStyle, ...((TextInput as any).defaultProps.style || {}) }
  } else {
    ;(TextInput as any).defaultProps = { style: defaultStyle }
  }

  // Patch Text.render to dynamically merge font family based on custom style props
  const oldTextRender = (Text as any).render
  if (oldTextRender) {
    ;(Text as any).render = function render(...args: any[]) {
      const origin = oldTextRender.apply(this, args)
      const family = getFontFamily(origin.props.style)
      const flattenedStyle = StyleSheet.flatten(origin.props.style) || {}
      return React.cloneElement(origin, {
        style: { ...flattenedStyle, fontFamily: family }
      })
    }
  }

  // Patch TextInput.render to dynamically merge font family based on custom style props
  const oldTextInputRender = (TextInput as any).render
  if (oldTextInputRender) {
    ;(TextInput as any).render = function render(...args: any[]) {
      const origin = oldTextInputRender.apply(this, args)
      const family = getFontFamily(origin.props.style)
      const flattenedStyle = StyleSheet.flatten(origin.props.style) || {}
      return React.cloneElement(origin, {
        style: { ...flattenedStyle, fontFamily: family }
      })
    }
  }
}

overrideDefaultFont()

/**
 * 0: full screen
 * 1: screen splits into 2 parts (intro part on left side and app part on the right side) when the screen larger than 960px
 * 2: screen sits on the center and its max size is 768px
 */
type ScreenMode = 0 | 1 | 2
const SCREEN_MODE: ScreenMode = 1

const handleImagePress = () => {
  // Open the website in the device's default web browser
  Linking.openURL(
    'https://play.google.com/store/apps/details?id=com.fmssolutions.onboardingapp'
  )
}
const handleImagePress1 = () => {
  // Open the website in the device's default web browser
  Linking.openURL('https://apps.apple.com/us/app/workhappy/id6450463466')
}

const App = (): JSX.Element => {
  const { width: DEVICE_WIDTH } = useWindowDimensions()

  const auth = useAuthStore()

  const [fontsLoaded] = useFonts({
    BaiJamjuree_700Bold,
    BaiJamjuree_300Light,
    BaiJamjuree_300Light_Italic,
    BaiJamjuree_400Regular,
    BaiJamjuree_400Regular_Italic,
    BaiJamjuree_600SemiBold
  })

  if (!fontsLoaded) return null

  return (
    <>

      <SafeAreaView
        style={[
          globalStyles.flexNone,
          auth.token
            ? globalStyles.primaryBgColor
            : globalStyles.lighterBgColor
        ]}
      />
      <SafeAreaView
        style={[
          globalStyles.flexOne,
          globalStyles.hDirection,
          auth.token
            ? globalStyles.lighterBgColor
            : globalStyles.lighterBgColor
        ]}
      >
        {SCREEN_MODE === 1 && DEVICE_WIDTH > 960 && (
          <View
            style={[
              globalStyles.flexOne,
              globalStyles.hCenterItems,
              globalStyles.whiteBgColor,
              styles.introWrapper
            ]}
          >
            <View style={[styles.intro]}>
              <View style={[globalStyles.mb8]}>
                <Image
                  source={require('./src/static/images/workhappy_logo1.png')}
                  style={[styles.appBrand, globalStyles.mb2]}
                />
                <View>
                  <Text
                    style={[
                      globalStyles.text,
                      globalStyles.primaryTextColor,
                      globalStyles.fs30,
                      globalStyles.fwBold,
                      globalStyles.mb2
                    ]}
                  >
                    Onboarding Portal
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row'
                    }}
                  >
                    <TouchableOpacity onPress={handleImagePress}>
                      <Image
                        source={require('./src/static/images/playstore.png')}
                        style={[
                          styles.appBrand1,
                          globalStyles.mb2,
                          { marginRight: 10 }
                        ]}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={handleImagePress1}>
                      <Image
                        source={require('./src/static/images/Appstore.png')}
                        style={[styles.appBrand1, globalStyles.mb2]}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
                <View
                  style={[
                    globalStyles.hDirection,
                    globalStyles.vCenterItems,
                    globalStyles.mt8
                  ]}
                >
                  <Text
                    style={[
                      globalStyles.text,
                      globalStyles.darkTextColor,
                      globalStyles.fs12,
                      globalStyles.fwLight,
                      globalStyles.mt4,
                    ]}
                  >
                    Powered by
                  </Text>
                  <Image
                    source={require('./src/static/images/fms_logo_3x.png')}
                    style={[styles.introLogo]}
                  />
                </View>
              </View>
            </View>
          </View>
        )}
        <View
          style={[
            globalStyles.flexOne,
            globalStyles.hCenterItems,
            SCREEN_MODE === 2 && DEVICE_WIDTH > 768 && styles.largeScreen
          ]}
        >
          <Router />
        </View>
      </SafeAreaView>
      <Toast config={toastConfig} />
    </>
  )
}

const styles = StyleSheet.create({
  introLogo: { width: 80, height: 80 },
  introWrapper: { borderRightColor: 'rgb(238, 238, 238)', borderRightWidth: 1 },
  intro: { paddingHorizontal: 60 },
  largeScreen: {
    maxWidth: 768,
    marginLeft: 'auto',
    marginRight: 'auto',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colorPalette.light
  },
  appBrand: { width: 180, height: 100, marginLeft: -10 },
  appBrand1: { width: 140, height: 140 }
})

export default App
